<template>
    <div>
        <BaseHero
            :title="hero?.title"
            :description="hero?.subtitle"
            class="base-hero--overview"
        >
            <div class="base-hero__filters">
                <SelectButton
                    v-model="selectedNeighbourhoods"
                    :options="neighbourhoodOptionsWithClear"
                    option-label="title"
                    option-value="id"
                    unstyled
                    class="select-button hidden-mobile-tablet"
                >
                    <template #option="{ option }">
                        <BaseChip
                            element="span"
                            :active="selectedNeighbourhoods === option.id"
                            :with-icon="false"
                        >
                            {{ option.title }}
                        </BaseChip>
                    </template>
                </SelectButton>

                <Select
                    v-model="selectedNeighbourhoods"
                    :options="neighbourhoodOptions"
                    option-label="title"
                    option-value="id"
                    show-clear
                    placeholder="Filter op buurt"
                    class="hidden-desktop"
                />

                <div class="base-hero__filter-group">
                    <Select
                        v-if="typeOptions?.length > 0"
                        v-model="selectedTypes"
                        :options="typeOptions"
                        option-label="title"
                        option-value="id"
                        show-clear
                        placeholder="Filter op type"
                    >
                        <template #dropdownicon>
                            <Icon name="app:expand_more" />
                        </template>
                    </Select>

                    <Select
                        v-model="selectedMonth"
                        :options="monthOptions"
                        option-label="label"
                        option-value="value"
                        show-clear
                        placeholder="Filter op maand"
                        class="month-filter"
                        :overlay-style="{ textTransform: 'capitalize' }"
                    >
                        <template #dropdownicon>
                            <Icon name="app:calendar_month" />
                        </template>
                    </Select>
                </div>
            </div>
        </BaseHero>

        <ContentOverview
            v-model:current-page="currentPage"
            sections="agenda"
            :is-between="selectedMonth ? ['dateRange', 'detail', selectedMonthStart ?? null, selectedMonthEnd ?? null] : undefined"
            order-by="dateRange.start asc"
            :is-not-past="['dateRange', 'detail', true]"
            :related-to
        />
    </div>
</template>

<script setup>
import { useDateFormat } from '@vueuse/core';
import { addMonths, format, isThisYear, parse, startOfMonth, endOfMonth } from 'date-fns';
import { nl } from 'date-fns/locale';

import AgendaOverviewQuery from '~/graphql/queries/Entry.graphql';

const { variables: defaultVariables }  = useCraftGraphql();

const variables = computed(() => {
    return {
        ...defaultVariables,
        uri: undefined,
        section: 'agendaOverview'
    };
});

const response = await useAsyncQuery({
    query: AgendaOverviewQuery,
    variables
});

const hero = computed(() => {
    return entry?.value?.defaultHero?.[0] ?? null;
});

const { craftEntry: entry } = useSeoCraftPage(response);

defineI18nRoute({
    paths: {
        en: '/events',
        nl: '/agenda',
    },
});

const currentPage = useCurrentPage();

// Neighbourhood
const { options: neighbourhoodOptions, selected: selectedNeighbourhoods } = await useCategory(
    'neighbourhood',
    'neighbourhood',
    currentPage
);

const neighbourhoodOptionsWithClear = computed(() => {
    return [
        { title: 'Heel Amsterdam', id: undefined },
        ...(neighbourhoodOptions.value ?? [])
    ];
});

// Type
const { options: typeOptions, selected: selectedTypes } = await useCategory(
    'agendaType',
    'type',
    currentPage
);

const { locale: activeLocale } = useI18n();

const decodeDate = (string) => {
    return parse(string, 'MM-yyyy', new Date());
};

const route = useRoute();
const selectedMonth = ref(route?.query?.maand ? decodeDate(route.query.maand) : null);

syncRoute(useDateFormat(selectedMonth, 'MM-YYYY'), 'maand', { page: 1 });
watch(selectedMonth, () => {
    currentPage.value = 1;
});

const selectedMonthStart = computed(() => selectedMonth.value ? startOfMonth(selectedMonth.value) : null);
const selectedMonthEnd = computed(() => selectedMonth.value ? endOfMonth(selectedMonth.value) : null);

const monthOptions = computed(() => {
    const referenceDate = startOfMonth(new Date());
    const options = [];

    for (let i = 0; i < 12; i++) {
        const monthDate = addMonths(referenceDate, i); // create date {x} months ahead

        options.push({
            label: format(
                monthDate,
                isThisYear(monthDate) ? 'MMMM' : 'MMMM yyyy', // add year only when not current year
                { locale: activeLocale.value === 'nl' ? nl : null }
            ),
            value: monthDate,
        });
    }

    return options;
});

// FILTER FOR GQL
const relatedTo = computed(() => {
    if (!selectedNeighbourhoods.value?.length && !selectedTypes.value?.length) {
        return null;
    }

    return [
        'and',
        selectedNeighbourhoods.value ?? undefined,
        selectedTypes.value ?? undefined,
        // add more if needed.
    ].filter(v=>v);
});

const { push } = useTagmanager();
watch(selectedNeighbourhoods, (v) => {
    const label = neighbourhoodOptions.value?.find(({ id }) => id === v)?.title ?? 'Heel amsterdam';

    return push({
        event: 'filter_clicked',
        filter_category: 'neighbourhood',
        click_text: label
    });
});
watch(selectedTypes, (v) => {
    const label = typeOptions.value?.find(({ id }) => id === v)?.title ?? '(onbekend)';

    return  push({
        event: 'filter_clicked',
        filter_category: 'projectType',
        click_text: label
    });
});
watch(selectedMonth, (v) => {
    const label = monthOptions.value?.find(({ value }) => value === v)?.label ?? '(onbekend)';

    return  push({
        event: 'filter_clicked',
        filter_category: 'month',
        click_text: label
    });
});

const generalStore = useGeneralStore();

generalStore.setPalette('palette--mma');
</script>

<style lang="less">
.month-filter {
    .p-select-label:not(.p-placeholder) {
        text-transform: capitalize;
    }
}
</style>
